import type { ShoppingListItem } from '@/types/shopping-list'

export interface AggregatedShoppingItem extends ShoppingListItem {
  recipe_ids: string[]
  recipe_titles: string[]
  category: string
  servings_factor?: number
  original_quantities?: { recipeId: string; quantity?: number; unit?: string }[]
  is_purchased?: boolean
  notes?: string | null
}

export interface ShoppingListCategoryGroup {
  category: string
  items: AggregatedShoppingItem[]
  total_items: number
  purchased_items: number
}

export const INGREDIENT_CATEGORIES = {
  groenten_verse: [
    'ui', 'uien', 'knoflook', 'teen', 'tomaat', 'tomaten', 'paprika', 'courgette',
    'aubergine', 'wortel', 'wortels', 'aardappel', 'aardappelen', 'sla', 'spinazie',
    'broccoli', 'bloemkool', 'prei', 'selderij', 'champignons', 'paddenstoelen',
    'avocado', 'komkommer', 'radijs', 'rucola', 'basilicum', 'peterselie', 'dille',
    'tijm', 'rozemarijn', 'oregano', 'bieslook', 'koriander', 'citroengras',
  ],
  vlees_vis: [
    'kip', 'kipfilet', 'gehakt', 'rundvlees', 'varkensvlees', 'spek', 'bacon',
    'zalm', 'tonijn', 'kabeljauw', 'garnalen', 'mosselen', 'vis', 'vlees',
    'worst', 'ham', 'salami', 'chorizo', 'kalkoen',
  ],
  zuivel_eieren: [
    'melk', 'room', 'slagroom', 'karnemelk', 'yoghurt', 'kwark', 'kaas',
    'mozzarella', 'parmezaan', 'feta', 'geitenkaas', 'roomkaas', 'mascarpone',
    'ei', 'eieren', 'boter', 'margarine',
  ],
  fruit: [
    'appel', 'appels', 'banaan', 'bananen', 'sinaasappel', 'sinaasappels',
    'citroen', 'citroenen', 'limoen', 'limoenen', 'druiven', 'aardbeien',
    'frambozen', 'bosbessen', 'mango', 'ananas', 'kiwi', 'peer', 'peren',
  ],
  granen_koolhydraten: [
    'rijst', 'pasta', 'spaghetti', 'macaroni', 'penne', 'fusilli', 'brood',
    'volkoren', 'bloem', 'meel', 'havermout', 'quinoa', 'couscous', 'bulgur',
    'aardappelen', 'noedels', 'crackers', 'beschuit',
  ],
  voorraadkast: [
    'olijfolie', 'olie', 'azijn', 'balsamico', 'zout', 'peper', 'suiker',
    'honing', 'mosterd', 'ketchup', 'mayonaise', 'sojasaus', 'tabasco',
    'bouillon', 'bouillonblokje', 'tomatenpuree', 'kokosmelk', 'blik',
    'conserven', 'kruiden', 'specerijen', 'vanille', 'kaneel', 'paprikapoeder',
  ],
  dranken: [
    'water', 'sap', 'sinaasappelsap', 'appelsap', 'wijn', 'bier', 'koffie',
    'thee', 'frisdrank', 'cola', 'limonade',
  ],
  diepvries: ['diepvries', 'bevroren', 'ijsblokjes', 'sorbet', 'ijs'],
  snacks_zoet: [
    'chocolade', 'koekjes', 'chips', 'noten', 'amandelen', 'walnoten',
    'pinda', 'rozijnen', 'dadels', 'crackers', 'popcorn',
  ],
} as const

export const UNIT_CONVERSIONS: Record<string, number> = {
  ml: 1,
  cl: 10,
  dl: 100,
  liter: 1000,
  l: 1000,
  gram: 1,
  g: 1,
  gr: 1,
  kilogram: 1000,
  kg: 1000,
  kilo: 1000,
  eetlepel: 15,
  el: 15,
  tbsp: 15,
  theelepel: 5,
  tl: 5,
  tsp: 5,
}

export const categorizeIngredient = (itemName: string): string => {
  const lowerName = itemName.toLowerCase()

  for (const [category, keywords] of Object.entries(INGREDIENT_CATEGORIES)) {
    for (const keyword of keywords) {
      if (lowerName.includes(keyword)) {
        return category
      }
    }
  }

  return 'overig'
}

export const groupByCategory = (items: AggregatedShoppingItem[]): ShoppingListCategoryGroup[] => {
  const categoryMap = new Map<string, AggregatedShoppingItem[]>()

  for (const item of items) {
    if (!categoryMap.has(item.category)) {
      categoryMap.set(item.category, [])
    }
    categoryMap.get(item.category)!.push(item)
  }

  const categoryOrder = [
    'groenten_verse',
    'vlees_vis',
    'zuivel_eieren',
    'fruit',
    'granen_koolhydraten',
    'voorraadkast',
    'dranken',
    'diepvries',
    'snacks_zoet',
    'overig',
  ]

  const groups: ShoppingListCategoryGroup[] = []

  for (const category of categoryOrder) {
    const categoryItems = categoryMap.get(category)
    if (categoryItems && categoryItems.length > 0) {
      categoryItems.sort((a, b) => a.name.localeCompare(b.name))
      groups.push({
        category,
        items: categoryItems,
        total_items: categoryItems.length,
        purchased_items: categoryItems.filter((item) => item.is_purchased).length,
      })
    }
  }

  for (const [category, categoryItems] of categoryMap) {
    if (!categoryOrder.includes(category)) {
      categoryItems.sort((a, b) => a.name.localeCompare(b.name))
      groups.push({
        category,
        items: categoryItems,
        total_items: categoryItems.length,
        purchased_items: categoryItems.filter((item) => item.is_purchased).length,
      })
    }
  }

  return groups
}

export const getCategoryDisplayName = (category: string): string => {
  const displayNames: Record<string, string> = {
    groenten_verse: 'Groenten & Verse Producten',
    vlees_vis: 'Vlees & Vis',
    zuivel_eieren: 'Zuivel & Eieren',
    fruit: 'Fruit',
    granen_koolhydraten: 'Granen & Koolhydraten',
    voorraadkast: 'Voorraadkast',
    dranken: 'Dranken',
    diepvries: 'Diepvries',
    snacks_zoet: 'Snacks & Zoet',
    overig: 'Overig',
  }

  return displayNames[category] || category
}
